import io from 'socket.io-client';
import { getApiBaseUrl } from '../utils/apiConfig';
import { getAuthToken } from '../utils/authUtils';

const debugLog = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

/**
 * Socket service for handling real-time communication
 */
class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.connectCallbacks = [];
    this.disconnectCallbacks = [];
    this.briefStatusCallbacks = {};
    this.initialized = false;
    this.reconnecting = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = Infinity; // Infinite reconnection attempts
    this.reconnectTimer = null;
    this.eventQueue = []; // Queue events while disconnected
    this.lastEventIds = {};
    this.connectionState = 'disconnected'; // 'disconnected', 'connecting', 'connected', 'reconnecting'
    this.heartbeatInterval = null;
    this.lastHeartbeat = Date.now();
    this.heartbeatTimeout = 30000; // 30 seconds
    this.missedHeartbeats = 0;
    this.missedEvents = new Map(); // Initialize missedEvents Map
    this.campaignRooms = new Map(); // Initialize campaignRooms Map for tracking joined rooms
    this.campaignHandlerCounts = new Map(); // Track generic campaign handlers by room

    // Add campaign events
    this.EVENTS = {
      CAMPAIGN_UPDATED: 'campaign:updated',
      CAMPAIGN_CREATORS_UPDATED: 'campaign:creators:updated',
      CAMPAIGN_ANALYTICS_UPDATED: 'campaign:analytics:updated',
      CAMPAIGN_DELETED: 'campaign:deleted',
      CREATOR_STATUS_CHANGED: 'creator:status:changed',
      CREATOR_BRIEFING_STATUS_CHANGED: 'creator:briefing:status:changed',
      CREATOR_DELIVERY_STATUS_CHANGED: 'creator:delivery:status:changed',
      CAMPAIGN_METRICS_UPDATED: 'campaign:metrics:updated',
      // New events from backend
      CREATOR_TWEET_SUBMITTED: 'creator:tweet:submitted',
      CREATOR_REMOVED: 'creator:removed',
      CREATOR_BATCH_PROGRESS: 'creator:batch:progress',
      // Campaign duplication events
      CAMPAIGN_DUPLICATION_PROGRESS: 'campaignDuplication:progress',
      CAMPAIGN_DUPLICATION_COMPLETED: 'campaignDuplication:completed',
      CAMPAIGN_DUPLICATION_ERROR: 'campaignDuplication:error',
      // Admin events
      ADMIN_EVENT: 'admin:event',
      ADMIN_STATS: 'admin:stats'
    };
    
    // Campaign event callbacks
    this.campaignCallbacks = {
      updated: {},
      creatorsUpdated: {},
      analyticsUpdated: {},
      deleted: {},
      creatorStatusChanged: {},
      creatorBriefingStatusChanged: {},
      creatorDeliveryStatusChanged: {},
      metricsUpdated: {},
      // Campaign duplication callbacks  
      duplicationProgress: {},
      duplicationCompleted: {},
      duplicationError: {}
    };
    
    // Admin event callbacks
    this.adminEventCallbacks = [];
  }

  /**
   * Connect to the socket server
   * @param {boolean} isReconnect - Whether this is a reconnection attempt
   * @returns {void}
   */
  connect(isReconnect = false) {
    // Clear any existing reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.socket?.connected && this.connected) {
      debugLog('Socket already connected');
      return;
    }

    if (this.socket) {
      if (this.connectionState === 'connecting' || this.connectionState === 'reconnecting' || this.socket.active) {
        debugLog(`Socket connection already ${this.connectionState}; reusing existing socket`);
        return;
      }

      // A stale disconnected socket can keep old listeners/reconnect state alive.
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this._resetListenerFlags();
    }

    // Update connection state
    this.connectionState = isReconnect ? 'reconnecting' : 'connecting';
    if (isReconnect) {
      this.reconnecting = true;
      this.reconnectAttempts++;
      debugLog(`Reconnection attempt ${this.reconnectAttempts} of ${this.maxReconnectAttempts}...`);
    } else {
      this.reconnecting = false;
      this.reconnectAttempts = 0;
      debugLog('Connecting to socket server...');
    }

    try {
      // Get the socket URL from environment or fall back to API URL
      const socketUrl = process.env.REACT_APP_SOCKET_URL || getApiBaseUrl();
      const isStaging = window.location.hostname.includes('staging');

      debugLog(`Initializing socket connection to ${socketUrl} in ${isStaging ? 'staging' : 'local'} environment`);

      // CRITICAL: Reset all _hasListener flags when creating a new socket
      // This ensures event listeners are re-registered on the new socket instance
      if (isReconnect || !this.socket) {
        this._resetListenerFlags();
      }

      // Configure socket options with environment-specific settings
      // IMPORTANT: Always fetch fresh token on every connection attempt
      const socketOptions = {
        transports: ['polling', 'websocket'],
        upgrade: true,
        // Use Socket.IO built-in reconnection for stability
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 30000,
        reconnectionAttempts: Infinity,
        timeout: 35000, // Connection timeout - slightly above pingTimeout + pingInterval
        forceNew: isReconnect, // Only force new connection on explicit reconnect
        auth: {
          token: getAuthToken() // Fresh token fetched on each connection
        }
      };
      
      // Log socket connection options in development without serializing in production
      debugLog('Socket connection options:', socketOptions);
      
      // Create socket connection with the configured options
      this.socket = io(socketUrl, socketOptions);
      
      // Get token for logging
      const token = getAuthToken();
      
      // Log authentication status
      debugLog(`Socket connecting with auth: ${token ? 'Yes' : 'No'}`);
      
      // Handle authentication errors
      this.socket.on('connect_error', (error) => {
        console.error('Socket connection error:', error.message);
        this.connected = false;
        this.connectionState = 'disconnected';

        // Stop custom heartbeat (Socket.IO built-in reconnection handles retries)
        this._stopHeartbeat();
      });
      
      // Set up connection event handlers
      this.socket.on('connect', () => {
        debugLog('Socket connected successfully');
        this.connected = true;
        this.connectionState = 'connected';
        this.reconnecting = false;
        this.reconnectAttempts = 0;

        // Rejoin all tracked campaign rooms
        this._rejoinRooms();

        // Flush event queue if reconnecting
        if (isReconnect && this.eventQueue.length > 0) {
          debugLog(`Flushing ${this.eventQueue.length} queued events...`);
          this._flushEventQueue();
        }

        // Start heartbeat monitoring
        this._startHeartbeat();

        // Notify all callbacks
        this.connectCallbacks.forEach(callback => callback());

        // Show reconnection notification if this was a reconnection
        if (isReconnect) {
          this._showReconnectionNotification();
        }
      });

      this.socket.on('auth:success', () => {
        this._rejoinRooms();
      });
      
      this.socket.on('disconnect', (reason) => {
        debugLog(`Socket disconnected: ${reason}`);
        this.connected = false;
        this.connectionState = 'disconnected';

        // Stop heartbeat
        this._stopHeartbeat();

        // Notify all callbacks
        this.disconnectCallbacks.forEach(callback => callback());

        // Socket.IO built-in reconnection handles reconnecting automatically
        // (reconnection: true in socket options)
      });
      
      this.socket.on('error', (error) => {
        console.error('Socket error:', error);
      });
      
      // Handle missed events from server
      this.socket.on('missed_events', (events) => {
        debugLog(`Received ${events.length} missed events`);
        this._processMissedEvents(events);
      });
      
      // Set initialized flag
      this.initialized = true;
    } catch (error) {
      console.error('Error initializing socket connection:', error);
      this.connected = false;
    }
  }

  /**
   * Disconnect from the socket server
   * @returns {void}
   */
  disconnect() {
    // Clear any reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Stop heartbeat
    this._stopHeartbeat();

    // Clear event queue
    this.eventQueue = [];

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.initialized = false;
      this.connectionState = 'disconnected';
      this.reconnecting = false;
      this.reconnectAttempts = 0;

      // CRITICAL: Reset listener flags so they get re-registered on next connect
      this._resetListenerFlags();
    }
  }

  /**
   * Reconnect with fresh authentication token
   * Call this after login to ensure socket uses the new token
   * @returns {void}
   */
  reconnectWithNewToken() {
    debugLog('Reconnecting socket with fresh auth token...');

    // Force disconnect (won't auto-reconnect due to 'io client disconnect' reason)
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    // Reset state
    this.connected = false;
    this.initialized = false;
    this.connectionState = 'disconnected';
    this._resetListenerFlags();

    // Connect with fresh token
    this.connect(false);
  }

  /**
   * Check if socket is connected
   * @returns {boolean}
   */
  isConnected() {
    return this.connected && this.socket && this.socket.connected;
  }

  _ensureSocket() {
    if (!this.socket) {
      this.connect();
    }

    return this.socket;
  }

  _hasCampaignHandlers(campaignId) {
    if (this.campaignHandlerCounts.get(campaignId) > 0) {
      return true;
    }

    return Object.values(this.campaignCallbacks).some(callbackGroup => {
      if (!callbackGroup || typeof callbackGroup !== 'object') {
        return false;
      }

      const callbacks = callbackGroup[campaignId];
      return Array.isArray(callbacks) && callbacks.length > 0;
    });
  }

  _ensureBriefStatusListener() {
    const socket = this._ensureSocket();
    if (!socket || this.briefStatusCallbacks._hasListener) {
      return socket;
    }

    socket.on('brief:status:updated', (data) => {
      const briefId = data.briefId;

      // Call global callbacks
      if (this.briefStatusCallbacks['global']) {
        this.briefStatusCallbacks['global'].forEach(cb => cb(data));
      }

      // Call brief-specific callbacks
      if (briefId && this.briefStatusCallbacks[briefId]) {
        this.briefStatusCallbacks[briefId].forEach(cb => cb(data));
      }
    });

    this.briefStatusCallbacks._hasListener = true;
    return socket;
  }

  /**
   * Register a callback for when socket connects
   * @param {Function} callback 
   * @returns {Function} Unsubscribe function
   */
  onConnect(callback) {
    if (typeof callback === 'function') {
      this.connectCallbacks.push(callback);
      
      // If already connected, call the callback immediately
      if (this.connected) {
        callback();
      }
      
      // Return unsubscribe function
      return () => {
        this.connectCallbacks = this.connectCallbacks.filter(cb => cb !== callback);
      };
    }
    return () => {};
  }
  
  /**
   * Register a callback for connection state changes
   * @param {Function} callback - Callback function with state parameter
   * @returns {Function} Unsubscribe function
   */
  onConnectionStateChange(callback) {
    if (typeof callback === 'function') {
      // Create a special wrapper that includes the state
      const wrappedCallback = () => callback(this.connectionState);
      this.connectCallbacks.push(wrappedCallback);
      this.disconnectCallbacks.push(wrappedCallback);
      
      // Call immediately with current state
      callback(this.connectionState);
      
      // Return unsubscribe function
      return () => {
        this.connectCallbacks = this.connectCallbacks.filter(cb => cb !== wrappedCallback);
        this.disconnectCallbacks = this.disconnectCallbacks.filter(cb => cb !== wrappedCallback);
      };
    }
    return () => {};
  }
  
  /**
   * Remove a connect callback
   * @param {Function} callback - The callback to remove
   */
  offConnect(callback) {
    if (typeof callback === 'function') {
      this.connectCallbacks = this.connectCallbacks.filter(cb => cb !== callback);
    }
  }

  /**
   * Register a callback for when socket disconnects
   * @param {Function} callback 
   * @returns {Function} Unsubscribe function
   */
  onDisconnect(callback) {
    if (typeof callback === 'function') {
      this.disconnectCallbacks.push(callback);
      
      // Return unsubscribe function
      return () => {
        this.disconnectCallbacks = this.disconnectCallbacks.filter(cb => cb !== callback);
      };
    }
    return () => {};
  }
  
  /**
   * Remove a disconnect callback
   * @param {Function} callback - The callback to remove
   */
  offDisconnect(callback) {
    if (typeof callback === 'function') {
      this.disconnectCallbacks = this.disconnectCallbacks.filter(cb => cb !== callback);
    }
  }

  /**
   * Join a campaign room to receive updates for a specific campaign
   * @param {string} campaignId
   * @returns {void}
   */
  joinCampaignRoom(campaignId) {
    if (!campaignId) return;

    // Track this room for rejoin on reconnection
    this.campaignRooms.set(campaignId, true);

    if (this.socket && this.connected) {
      this.socket.emit('join:campaign', { campaignId });
      debugLog(`Joined campaign room: ${campaignId}`);
    } else if (!this.socket) {
      debugLog(`Queued campaign room join for when connected: ${campaignId}`);

      // Try to connect
      this.connect();
    }
  }

  /**
   * Leave a campaign room
   * @param {string} campaignId 
   * @returns {void}
   */
  leaveCampaignRoom(campaignId) {
    if (this.socket && campaignId) {
      this.socket.emit('leave:campaign', { campaignId });
      debugLog(`Left campaign room: ${campaignId}`);

      // Remove any queued join for this room (safely check if missedEvents exists)
      if (this.missedEvents && this.missedEvents.delete) {
        this.missedEvents.delete(`join:campaign:${campaignId}`);
      }

      // Remove from campaign rooms tracking
      if (this.campaignRooms && this.campaignRooms.delete) {
        this.campaignRooms.delete(campaignId);
      }
    }
  }
  
  /**
   * Get the connection state
   * @returns {string} The current connection state
   */
  getConnectionState() {
    return this.connectionState;
  }
  
  /**
   * @private
   * Removed: using Socket.IO built-in reconnection (reconnection: true in socket options)
   */
  _scheduleReconnect() {
    // Removed: using Socket.IO built-in reconnection
  }

  /**
   * Reset all _hasListener flags so event listeners get re-registered on new socket
   * CRITICAL: Must be called before creating a new socket instance (forceNew: true)
   * @private
   */
  _resetListenerFlags() {
    debugLog('Resetting listener flags for new socket instance');

    // Reset campaign callback listeners
    if (this.campaignCallbacks) {
      this.campaignCallbacks.updated._hasListener = false;
      this.campaignCallbacks.creatorsUpdated._hasListener = false;
      this.campaignCallbacks.analyticsUpdated._hasListener = false;
      this.campaignCallbacks.deleted._hasListener = false;
      this.campaignCallbacks.creatorStatusChanged._hasListener = false;
      this.campaignCallbacks.creatorBriefingStatusChanged._hasListener = false;
      this.campaignCallbacks.creatorDeliveryStatusChanged._hasListener = false;
      this.campaignCallbacks.metricsUpdated._hasListener = false;
      this.campaignCallbacks.duplicationProgress._hasListener = false;
      this.campaignCallbacks.duplicationCompleted._hasListener = false;
      this.campaignCallbacks.duplicationError._hasListener = false;
    }

    // Reset brief status callback listener
    if (this.briefStatusCallbacks) {
      this.briefStatusCallbacks._hasListener = false;
    }
  }
  
  /**
   * Show a user-friendly reconnection notification
   * @private
   */
  _showReconnectionNotification() {
    // Use toast notification if available
    if (typeof window !== 'undefined' && window.toast) {
      window.toast.success('Reconnected to server');
    } else {
      debugLog('Reconnected to server');
    }
  }

  /**
   * Rejoin all tracked campaign rooms after reconnection
   * @private
   */
  _rejoinRooms() {
    if (!this.socket || !this.connected) return;

    const roomCount = this.campaignRooms.size;
    if (roomCount === 0) return;

    debugLog(`Rejoining ${roomCount} campaign room(s)...`);

    // Rejoin all tracked campaign rooms
    for (const campaignId of this.campaignRooms.keys()) {
      this.socket.emit('join:campaign', { campaignId });
      debugLog(`Rejoined campaign room: ${campaignId}`);
    }
  }

  /**
   * Process missed events received from the server
   * @param {Array} events - Array of missed events
   * @private
   */
  _processMissedEvents(events) {
    if (!Array.isArray(events) || events.length === 0) {
      return;
    }
    
    debugLog(`Processing ${events.length} missed events`);
    
    // Process each event
    events.forEach(event => {
      if (event && event.type && event.data) {
        // Update last event ID
        if (event.id) {
          this.lastEventIds[event.type] = event.id;
        }
        
        // Emit the event locally
        if (this.socket) {
          this.socket.emit(event.type, event.data);
        }
      }
    });
  }
  
  /**
   * Get or create a client ID for tracking
   * @returns {string} Client ID
   * @private
   */
  _getClientId() {
    try {
      let clientId = localStorage.getItem('socket_client_id');
      
      if (!clientId) {
        clientId = `client_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        localStorage.setItem('socket_client_id', clientId);
      }
      
      return clientId;
    } catch (error) {
      // If localStorage is not available, generate a temporary ID
      return `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    }
  }
  
  /**
   * Join a brief room to receive updates for a specific brief
   * @param {string} briefId 
   * @returns {void}
   */
  joinBriefRoom(briefId) {
    if (this.socket && briefId) {
      this.socket.emit('join:brief', { briefId });
      debugLog(`Joined brief room: ${briefId}`);
    }
  }

  /**
   * Leave a brief room
   * @param {string} briefId 
   * @returns {void}
   */
  leaveBriefRoom(briefId) {
    if (this.socket && briefId) {
      this.socket.emit('leave:brief', { briefId });
      debugLog(`Left brief room: ${briefId}`);
    }
  }

  /**
   * Register a callback for campaign updates
   * @param {string} campaignId - Campaign ID to listen for updates
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onCampaignUpdated(campaignId, callback) {
    if (!campaignId || typeof callback !== 'function') {
      return () => {};
    }

    const socket = this._ensureSocket();
    if (!socket) {
      return () => {};
    }
    
    // Initialize callback array if needed
    if (!this.campaignCallbacks.updated[campaignId]) {
      this.campaignCallbacks.updated[campaignId] = [];
    }
    
    // Add callback
    this.campaignCallbacks.updated[campaignId].push(callback);
    
    // Set up socket listener if not already set
    if (!this.campaignCallbacks.updated._hasListener) {
      socket.on(this.EVENTS.CAMPAIGN_UPDATED, (data) => {
        // Handle both formats: { campaign } and { campaignId, ... }
        const campaign = data.campaign || data;
        const id = campaign._id || campaign.id || data.campaignId;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Campaign update received:', {
            campaignId: id,
            pricingType: campaign.pricingType,
            hasFullCampaign: Boolean(data.campaign),
            hasUpdates: Boolean(data.updates),
            requiresRefresh: Boolean(data.requiresRefresh)
          });
        }
        
        // Call campaign-specific callbacks
        if (id && this.campaignCallbacks.updated[id]) {
          this.campaignCallbacks.updated[id].forEach(cb => cb(campaign));
        }
      });
      
      this.campaignCallbacks.updated._hasListener = true;
    }
    
    // Return unsubscribe function
    return () => {
      if (this.campaignCallbacks.updated[campaignId]) {
        this.campaignCallbacks.updated[campaignId] = 
          this.campaignCallbacks.updated[campaignId].filter(cb => cb !== callback);
      }
    };
  }
  
  /**
   * Register a callback for campaign creators updates
   * @param {string} campaignId - Campaign ID to listen for updates
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onCampaignCreatorsUpdated(campaignId, callback) {
    if (!campaignId || typeof callback !== 'function') {
      return () => {};
    }

    const socket = this._ensureSocket();
    if (!socket) {
      return () => {};
    }
    
    // Initialize callback array if needed
    if (!this.campaignCallbacks.creatorsUpdated[campaignId]) {
      this.campaignCallbacks.creatorsUpdated[campaignId] = [];
    }
    
    // Add callback
    this.campaignCallbacks.creatorsUpdated[campaignId].push(callback);
    
    // Set up socket listener if not already set
    if (!this.campaignCallbacks.creatorsUpdated._hasListener) {
      socket.on(this.EVENTS.CAMPAIGN_CREATORS_UPDATED, (data) => {
        const id = data.campaignId;
        
        // Call campaign-specific callbacks
        if (id && this.campaignCallbacks.creatorsUpdated[id]) {
          this.campaignCallbacks.creatorsUpdated[id].forEach(cb => cb(data));
        }
      });
      
      this.campaignCallbacks.creatorsUpdated._hasListener = true;
    }
    
    // Return unsubscribe function
    return () => {
      if (this.campaignCallbacks.creatorsUpdated[campaignId]) {
        this.campaignCallbacks.creatorsUpdated[campaignId] = 
          this.campaignCallbacks.creatorsUpdated[campaignId].filter(cb => cb !== callback);
      }
    };
  }
  
  /**
   * Register a callback for creator briefing status changes
   * @param {string} campaignId - Campaign ID to listen for updates
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onCreatorBriefingStatusChanged(campaignId, callback) {
    if (!campaignId || typeof callback !== 'function') {
      return () => {};
    }

    const socket = this._ensureSocket();
    if (!socket) {
      return () => {};
    }
    
    // Initialize callback array if needed
    if (!this.campaignCallbacks.creatorBriefingStatusChanged[campaignId]) {
      this.campaignCallbacks.creatorBriefingStatusChanged[campaignId] = [];
    }
    
    // Add callback
    this.campaignCallbacks.creatorBriefingStatusChanged[campaignId].push(callback);
    
    // Set up socket listener if not already set
    if (!this.campaignCallbacks.creatorBriefingStatusChanged._hasListener) {
      socket.on(this.EVENTS.CREATOR_BRIEFING_STATUS_CHANGED, (data) => {
        const id = data.campaignId;
        
        // Call campaign-specific callbacks
        if (id && this.campaignCallbacks.creatorBriefingStatusChanged[id]) {
          this.campaignCallbacks.creatorBriefingStatusChanged[id].forEach(cb => cb(data));
        }
      });
      
      this.campaignCallbacks.creatorBriefingStatusChanged._hasListener = true;
    }
    
    // Return unsubscribe function
    return () => {
      if (this.campaignCallbacks.creatorBriefingStatusChanged[campaignId]) {
        this.campaignCallbacks.creatorBriefingStatusChanged[campaignId] = 
          this.campaignCallbacks.creatorBriefingStatusChanged[campaignId].filter(cb => cb !== callback);
      }
    };
  }
  
  /**
   * Register a callback for creator delivery status changes
   * @param {string} campaignId - Campaign ID to listen for updates
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onCreatorDeliveryStatusChanged(campaignId, callback) {
    if (!campaignId || typeof callback !== 'function') {
      return () => {};
    }

    const socket = this._ensureSocket();
    if (!socket) {
      return () => {};
    }
    
    // Initialize callback array if needed
    if (!this.campaignCallbacks.creatorDeliveryStatusChanged[campaignId]) {
      this.campaignCallbacks.creatorDeliveryStatusChanged[campaignId] = [];
    }
    
    // Add callback
    this.campaignCallbacks.creatorDeliveryStatusChanged[campaignId].push(callback);
    
    // Set up socket listener if not already set
    if (!this.campaignCallbacks.creatorDeliveryStatusChanged._hasListener) {
      socket.on(this.EVENTS.CREATOR_DELIVERY_STATUS_CHANGED, (data) => {
        const id = data.campaignId;
        
        // Call campaign-specific callbacks
        if (id && this.campaignCallbacks.creatorDeliveryStatusChanged[id]) {
          this.campaignCallbacks.creatorDeliveryStatusChanged[id].forEach(cb => cb(data));
        }
      });
      
      this.campaignCallbacks.creatorDeliveryStatusChanged._hasListener = true;
    }
    
    // Return unsubscribe function
    return () => {
      if (this.campaignCallbacks.creatorDeliveryStatusChanged[campaignId]) {
        this.campaignCallbacks.creatorDeliveryStatusChanged[campaignId] = 
          this.campaignCallbacks.creatorDeliveryStatusChanged[campaignId].filter(cb => cb !== callback);
      }
    };
  }
  
  /**
   * Register a callback for creator status changes (creator-specific status like "High Priority")
   * @param {string} campaignId - Campaign ID to listen for updates
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onCreatorStatusChanged(campaignId, callback) {
    if (!campaignId || typeof callback !== 'function') {
      return () => {};
    }

    const socket = this._ensureSocket();
    if (!socket) {
      return () => {};
    }
    
    // Initialize callback array if needed
    if (!this.campaignCallbacks.creatorStatusChanged[campaignId]) {
      this.campaignCallbacks.creatorStatusChanged[campaignId] = [];
    }
    
    // Add callback
    this.campaignCallbacks.creatorStatusChanged[campaignId].push(callback);
    
    // Set up socket listener if not already set
    if (!this.campaignCallbacks.creatorStatusChanged._hasListener) {
      socket.on(this.EVENTS.CREATOR_STATUS_CHANGED, (data) => {
        const id = data.campaignId;
        
        // Call campaign-specific callbacks
        if (id && this.campaignCallbacks.creatorStatusChanged[id]) {
          this.campaignCallbacks.creatorStatusChanged[id].forEach(cb => cb(data));
        }
      });
      
      this.campaignCallbacks.creatorStatusChanged._hasListener = true;
    }
    
    // Return unsubscribe function
    return () => {
      if (this.campaignCallbacks.creatorStatusChanged[campaignId]) {
        this.campaignCallbacks.creatorStatusChanged[campaignId] = 
          this.campaignCallbacks.creatorStatusChanged[campaignId].filter(cb => cb !== callback);
      }
    };
  }
  
  /**
   * Register a callback for campaign metrics updates
   * @param {string} campaignId - Campaign ID to listen for updates
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onCampaignMetricsUpdated(campaignId, callback) {
    if (!campaignId || typeof callback !== 'function') {
      return () => {};
    }

    const socket = this._ensureSocket();
    if (!socket) {
      return () => {};
    }
    
    // Initialize callback array if needed
    if (!this.campaignCallbacks.metricsUpdated[campaignId]) {
      this.campaignCallbacks.metricsUpdated[campaignId] = [];
    }
    
    // Add callback
    this.campaignCallbacks.metricsUpdated[campaignId].push(callback);
    
    // Set up socket listener if not already set
    if (!this.campaignCallbacks.metricsUpdated._hasListener) {
      socket.on(this.EVENTS.CAMPAIGN_METRICS_UPDATED, (data) => {
        const id = data.campaignId;
        
        // Call campaign-specific callbacks
        if (id && this.campaignCallbacks.metricsUpdated[id]) {
          this.campaignCallbacks.metricsUpdated[id].forEach(cb => cb(data));
        }
      });
      
      this.campaignCallbacks.metricsUpdated._hasListener = true;
    }
    
    // Return unsubscribe function
    return () => {
      if (this.campaignCallbacks.metricsUpdated[campaignId]) {
        this.campaignCallbacks.metricsUpdated[campaignId] = 
          this.campaignCallbacks.metricsUpdated[campaignId].filter(cb => cb !== callback);
      }
    };
  }

  /**
   * Register a callback for campaign analytics updates
   * @param {string} campaignId - Campaign ID to listen for updates
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onCampaignAnalyticsUpdated(campaignId, callback) {
    if (!campaignId || typeof callback !== 'function') {
      return () => {};
    }

    const socket = this._ensureSocket();
    if (!socket) {
      return () => {};
    }
    
    // Initialize callback array if needed
    if (!this.campaignCallbacks.analyticsUpdated[campaignId]) {
      this.campaignCallbacks.analyticsUpdated[campaignId] = [];
    }
    
    // Add callback
    this.campaignCallbacks.analyticsUpdated[campaignId].push(callback);
    
    // Set up socket listener if not already set
    if (!this.campaignCallbacks.analyticsUpdated._hasListener) {
      socket.on(this.EVENTS.CAMPAIGN_ANALYTICS_UPDATED, (data) => {
        const id = data.campaignId;
        
        // Call campaign-specific callbacks
        if (id && this.campaignCallbacks.analyticsUpdated[id]) {
          this.campaignCallbacks.analyticsUpdated[id].forEach(cb => cb(data));
        }
      });
      
      this.campaignCallbacks.analyticsUpdated._hasListener = true;
    }
    
    // Return unsubscribe function
    return () => {
      if (this.campaignCallbacks.analyticsUpdated[campaignId]) {
        this.campaignCallbacks.analyticsUpdated[campaignId] = 
          this.campaignCallbacks.analyticsUpdated[campaignId].filter(cb => cb !== callback);
      }
    };
  }

  /**
   * Register a callback for brief status updates
   * @param {Function} callback 
   * @returns {Function} Unsubscribe function
   */
  onBriefStatusUpdate(callback) {
    if (typeof callback === 'function') {
      // Store callback for all briefs
      if (!this.briefStatusCallbacks['global']) {
        this.briefStatusCallbacks['global'] = [];
      }
      this.briefStatusCallbacks['global'].push(callback);
      
      this._ensureBriefStatusListener();
      
      // Return unsubscribe function
      return () => {
        if (this.briefStatusCallbacks['global']) {
          this.briefStatusCallbacks['global'] = this.briefStatusCallbacks['global'].filter(cb => cb !== callback);
        }
      };
    }
    return () => {};
  }

  /**
   * Register a callback for a specific brief's status updates
   * @param {string} briefId 
   * @param {Function} callback 
   * @returns {Function} Unsubscribe function
   */
  onBriefStatusUpdateById(briefId, callback) {
    if (typeof callback === 'function' && briefId) {
      if (!this.briefStatusCallbacks[briefId]) {
        this.briefStatusCallbacks[briefId] = [];
      }
      this.briefStatusCallbacks[briefId].push(callback);

      this._ensureBriefStatusListener();
      
      // Return unsubscribe function
      return () => {
        this.briefStatusCallbacks[briefId] = this.briefStatusCallbacks[briefId]
          .filter(cb => cb !== callback);
      };
    }
    return () => {};
  }
  
  /**
   * Register a campaign event handler
   * @param {string} campaignId - Campaign ID
   * @param {string} eventType - Event type
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  registerCampaignHandler(campaignId, eventType, callback) {
    const socket = this._ensureSocket();
    if (!socket) {
      return () => {};
    }
    
    const eventId = `${eventType}:${campaignId}`;
    
    debugLog(`Registering handler for ${eventId}`);
    this.campaignHandlerCounts.set(campaignId, (this.campaignHandlerCounts.get(campaignId) || 0) + 1);
    
    // Create a wrapper that tracks the last event ID
    const wrappedCallback = (data) => {
      // Store the event ID for reconnection
      if (data && data.eventId) {
        this.lastEventIds[eventType] = data.eventId;
      }
      
      // Call the original callback
      callback(data);
    };
    
    // Register the event handler
    socket.on(eventId, wrappedCallback);
    
    // Join the campaign room
    this.joinCampaignRoom(campaignId);
    
    // Return unsubscribe function
    return () => {
      debugLog(`Unregistering handler for ${eventId}`);
      if (this.socket) {
        this.socket.off(eventId, wrappedCallback);
      }

      const remainingHandlers = (this.campaignHandlerCounts.get(campaignId) || 1) - 1;
      if (remainingHandlers > 0) {
        this.campaignHandlerCounts.set(campaignId, remainingHandlers);
      } else {
        this.campaignHandlerCounts.delete(campaignId);
      }
      
      // Leave the campaign room if no more handlers for this campaign
      if (!this._hasCampaignHandlers(campaignId)) {
        this.leaveCampaignRoom(campaignId);
      }
    };
  }  

  /**
   * Remove a campaign event handler
   * @param {string} campaignId - Campaign ID
   * @param {string} eventType - Event type from this.EVENTS
   * @param {Function} callback - Callback function
   */
  offCampaignHandler(campaignId, eventType, callback) {
    if (!this.socket) return;
    
    const eventId = `${eventType}:${campaignId}`;
    this.socket.off(eventId, callback);
  }
  
  /**
   * Check if WebSockets are enabled on the server
   * @returns {Promise<boolean>} - A promise that resolves to true if WebSockets are enabled
   */
  async checkWebSocketsEnabled() {
    try {
      // Try to connect if not already connected
      if (!this.socket) {
        this.connect();
      }
      
      // Wait for connection or timeout
      return new Promise((resolve) => {
        // If already connected, resolve immediately
        if (this.connected) {
          resolve(true);
          return;
        }
        
        // Set a timeout to resolve with false if connection fails
        const timeout = setTimeout(() => {
          resolve(false);
        }, 5000); // 5 second timeout
        
        // Set up one-time connect handler
        const connectHandler = () => {
          clearTimeout(timeout);
          resolve(true);
        };
        
        // Set up one-time error handler
        const errorHandler = () => {
          clearTimeout(timeout);
          resolve(false);
        };
        
        // Register handlers
        this.socket.once('connect', connectHandler);
        this.socket.once('connect_error', errorHandler);
        this.socket.once('error', errorHandler);
      });
    } catch (error) {
      console.error('Error checking WebSockets:', error);
      return false;
    }
  }
  
  /**
   * Register a handler for admin events
   * @param {Function} callback - Callback function that receives event data
   * @returns {Function} Unsubscribe function
   */
  registerAdminEventHandler(callback) {
    if (typeof callback !== 'function') {
      return () => {};
    }

    const socket = this._ensureSocket();
    if (!socket) {
      return () => {};
    }
    
    // Add to admin event callbacks
    this.adminEventCallbacks.push(callback);
    
    // Join admin room
    socket.emit('join:admin');
    
    // Set up listener for admin events
    socket.on(this.EVENTS.ADMIN_EVENT, callback);
    
    // Return unsubscribe function
    return () => {
      if (this.socket) {
        this.socket.off(this.EVENTS.ADMIN_EVENT, callback);
      }
      this.adminEventCallbacks = this.adminEventCallbacks.filter(cb => cb !== callback);
      
      // Leave admin room if no more callbacks
      if (this.socket && this.adminEventCallbacks.length === 0) {
        this.socket.emit('leave:admin');
      }
    };
  }

  // Additional methods can be added here
  /**
   * Remove an admin event handler
   * @param {Function} callback - The callback function to remove
   */
  offAdminEventHandler(callback) {
    if (this.socket) {
      this.socket.off(this.EVENTS.ADMIN_EVENT, callback);
      this.adminEventCallbacks = this.adminEventCallbacks.filter(cb => cb !== callback);
      
      // Leave admin room if no more callbacks
      if (this.adminEventCallbacks.length === 0) {
        this.socket.emit('leave:admin');
      }
    }
  }

  /**
   * Generic event listener registration
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  on(event, callback) {
    if (typeof callback !== 'function') {
      return () => {};
    }

    const socket = this._ensureSocket();
    if (socket) {
      socket.on(event, callback);
    }
    
    // Return unsubscribe function
    return () => {
      this.off(event, callback);
    };
  }

  /**
   * Remove a generic event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  off(event, callback) {
    if (this.socket && typeof callback === 'function') {
      this.socket.off(event, callback);
    }
  }

  /**
   * Register a callback for campaign duplication progress updates
   * @param {string} campaignId - Campaign ID to listen for progress
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onCampaignDuplicationProgress(campaignId, callback) {
    if (!campaignId || typeof callback !== 'function') {
      return () => {};
    }

    const socket = this._ensureSocket();
    if (!socket) {
      return () => {};
    }
    
    // Initialize callback array if needed
    if (!this.campaignCallbacks.duplicationProgress[campaignId]) {
      this.campaignCallbacks.duplicationProgress[campaignId] = [];
    }
    
    // Add callback
    this.campaignCallbacks.duplicationProgress[campaignId].push(callback);
    
    // Set up socket listener if not already set
    if (!this.campaignCallbacks.duplicationProgress._hasListener) {
      socket.on(this.EVENTS.CAMPAIGN_DUPLICATION_PROGRESS, (data) => {
        const { campaignId: id, jobId, progress } = data;
        
        debugLog('Campaign duplication progress received:', {
          campaignId: id,
          jobId,
          progress
        });
        
        if (id && this.campaignCallbacks.duplicationProgress[id]) {
          this.campaignCallbacks.duplicationProgress[id].forEach(callback => {
            try {
              callback(data);
            } catch (error) {
              console.error('Error in duplication progress callback:', error);
            }
          });
        }
      });
      
      this.campaignCallbacks.duplicationProgress._hasListener = true;
    }
    
    // Return unsubscribe function
    return () => {
      if (this.campaignCallbacks.duplicationProgress[campaignId]) {
        this.campaignCallbacks.duplicationProgress[campaignId] = 
          this.campaignCallbacks.duplicationProgress[campaignId].filter(cb => cb !== callback);
      }
    };
  }

  /**
   * Register a callback for campaign duplication completion
   * @param {string} campaignId - Campaign ID to listen for completion
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onCampaignDuplicationCompleted(campaignId, callback) {
    if (!campaignId || typeof callback !== 'function') {
      return () => {};
    }

    const socket = this._ensureSocket();
    if (!socket) {
      return () => {};
    }
    
    // Initialize callback array if needed
    if (!this.campaignCallbacks.duplicationCompleted[campaignId]) {
      this.campaignCallbacks.duplicationCompleted[campaignId] = [];
    }
    
    // Add callback
    this.campaignCallbacks.duplicationCompleted[campaignId].push(callback);
    
    // Set up socket listener if not already set
    if (!this.campaignCallbacks.duplicationCompleted._hasListener) {
      socket.on(this.EVENTS.CAMPAIGN_DUPLICATION_COMPLETED, (data) => {
        const { campaignId: id, jobId, result } = data;
        
        debugLog('Campaign duplication completed:', {
          campaignId: id,
          jobId,
          result
        });
        
        if (id && this.campaignCallbacks.duplicationCompleted[id]) {
          this.campaignCallbacks.duplicationCompleted[id].forEach(callback => {
            try {
              callback(data);
            } catch (error) {
              console.error('Error in duplication completion callback:', error);
            }
          });
        }
      });
      
      this.campaignCallbacks.duplicationCompleted._hasListener = true;
    }
    
    // Return unsubscribe function
    return () => {
      if (this.campaignCallbacks.duplicationCompleted[campaignId]) {
        this.campaignCallbacks.duplicationCompleted[campaignId] = 
          this.campaignCallbacks.duplicationCompleted[campaignId].filter(cb => cb !== callback);
      }
    };
  }

  /**
   * Register a callback for campaign duplication errors
   * @param {string} campaignId - Campaign ID to listen for errors
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onCampaignDuplicationError(campaignId, callback) {
    if (!campaignId || typeof callback !== 'function') {
      return () => {};
    }

    const socket = this._ensureSocket();
    if (!socket) {
      return () => {};
    }
    
    // Initialize callback array if needed
    if (!this.campaignCallbacks.duplicationError[campaignId]) {
      this.campaignCallbacks.duplicationError[campaignId] = [];
    }
    
    // Add callback
    this.campaignCallbacks.duplicationError[campaignId].push(callback);
    
    // Set up socket listener if not already set
    if (!this.campaignCallbacks.duplicationError._hasListener) {
      socket.on(this.EVENTS.CAMPAIGN_DUPLICATION_ERROR, (data) => {
        const { campaignId: id, jobId, error } = data;
        
        debugLog('Campaign duplication error:', {
          campaignId: id,
          jobId,
          error
        });
        
        if (id && this.campaignCallbacks.duplicationError[id]) {
          this.campaignCallbacks.duplicationError[id].forEach(callback => {
            try {
              callback(data);
            } catch (error) {
              console.error('Error in duplication error callback:', error);
            }
          });
        }
      });
      
      this.campaignCallbacks.duplicationError._hasListener = true;
    }
    
    // Return unsubscribe function
    return () => {
      if (this.campaignCallbacks.duplicationError[campaignId]) {
        this.campaignCallbacks.duplicationError[campaignId] = 
          this.campaignCallbacks.duplicationError[campaignId].filter(cb => cb !== callback);
      }
    };
  }

  /**
   * Emit an event (queues if disconnected)
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data) {
    if (this.socket && this.connected) {
      this.socket.emit(event, data);
    } else {
      // Queue the event with timestamp
      debugLog(`Queueing event '${event}' - socket not connected`);
      this.eventQueue.push({
        event,
        data,
        timestamp: Date.now()
      });

      // Limit queue size to prevent memory issues
      if (this.eventQueue.length > 100) {
        this.eventQueue.shift(); // Remove oldest event
      }
    }
  }

  /**
   * @private
   * Removed: using Socket.IO built-in ping/pong heartbeat
   */
  _startHeartbeat() {
    // Removed: using Socket.IO built-in ping/pong heartbeat
  }

  /**
   * @private
   * Removed: using Socket.IO built-in ping/pong heartbeat
   */
  _stopHeartbeat() {
    // Removed: using Socket.IO built-in ping/pong heartbeat
  }

  /**
   * Flush queued events
   * @private
   */
  _flushEventQueue() {
    if (!this.socket || !this.connected) return;

    const now = Date.now();
    const maxAge = 60000; // Don't send events older than 1 minute

    while (this.eventQueue.length > 0) {
      const queuedEvent = this.eventQueue.shift();

      // Skip old events
      if (now - queuedEvent.timestamp > maxAge) {
        debugLog(`Dropping old event '${queuedEvent.event}' (age: ${now - queuedEvent.timestamp}ms)`);
        continue;
      }

      // Emit the queued event
      this.socket.emit(queuedEvent.event, queuedEvent.data);
      debugLog(`Flushed queued event '${queuedEvent.event}'`);
    }
  }
}

// Create singleton instance
const socketServiceInstance = new SocketService();

// Export as both named and default export
export const socketService = socketServiceInstance;
export default socketServiceInstance;
